import styled from '@emotion/styled';
import { useState } from 'react';
import { DetailedScore, SingSetup, Song } from '~/interfaces';
import { typography } from '~/modules/Elements/cssMixins';
import useBackgroundMusic from '~/modules/hooks/useBackgroundMusic';
import useViewportSize from '~/modules/hooks/useViewportSize';
import ResultsView from '~/routes/Game/Singing/PostGame/Views/Results';
import { BackgroundThemeSetting, useSettingValue } from '~/routes/Settings/SettingsState';
import SongPage from '../../SongPage';
import HighScoresView from './Views/HighScores';

export interface PlayerScore {
  detailedScore: [DetailedScore, DetailedScore];
  playerNumber: 0 | 1 | 2 | 3;
  name: string;
}

interface HighScoreEntity {
  singSetupId: string;
  name: string;
  score: number;
  date: string;
}

interface Props {
  width: number;
  height: number;
  song: Song;
  onClickSongSelection: () => void;
  players: PlayerScore[];
  singSetup: SingSetup;
  highScores: HighScoreEntity[];
}

function PostGameView({ song, width, height, onClickSongSelection, players, highScores, singSetup }: Props) {
  const [backgroundTheme] = useSettingValue(BackgroundThemeSetting);
  useBackgroundMusic(true);

  const { width: vw } = useViewportSize();
  const isMobile = (vw ?? 9999) < 640;

  const [step, setStep] = useState<'results' | 'highscores'>('results');

  return (
    <SongPage songData={song} width={width} height={height}>
      <Container data-mobile={isMobile}>
        {step === 'results' && (
          <ResultsView
            onNextStep={() => setStep('highscores')}
            players={players}
            singSetup={singSetup}
            highScores={highScores}
          />
        )}

        {step === 'highscores' && (
          <HighScoresView
            onNextStep={onClickSongSelection}
            singSetup={singSetup}
            highScores={highScores}
            song={song}
          />
        )}
      </Container>

      {backgroundTheme === 'christmas' && (
        <ChristmasMusicCredit>
          Credit to <a href="https://www.FesliyanStudios.com">https://www.FesliyanStudios.com</a> for the background music.
        </ChristmasMusicCredit>
      )}
    </SongPage>
  );
}

export default PostGameView;

const Container = styled.div`
  pointer-events: auto;
  position: relative;
  width: 100%;
  height: 100%;
`;

const ChristmasMusicCredit = styled.span`
  position: fixed;
  bottom: 2rem;
  left: 2rem;
  font-size: 1.5rem;
  ${typography};
`;